import React, { useCallback, useRef, useState } from "react";
import { GripHorizontal, X } from "lucide-react";

// 悬浮窗：自由拖拽 + 八向调整大小 + 双击标题复原默认位置；关闭后重开即回默认位。
const defRect = () => {
  const vw = window.innerWidth, vh = window.innerHeight;
  const w = Math.min(920, vw - 32), h = Math.min(Math.round(vh * 0.75), 720);
  return { x: Math.max(16, Math.round((vw - w) / 2)), y: Math.max(12, Math.round((vh - h) / 2)), w, h };
};

let stackCount = 0;

const HANDLES = [
  { dir: "n", cls: "top-0 inset-x-3 h-1.5 cursor-n-resize" },
  { dir: "s", cls: "bottom-0 inset-x-3 h-1.5 cursor-s-resize" },
  { dir: "w", cls: "left-0 inset-y-3 w-1.5 cursor-w-resize" },
  { dir: "e", cls: "right-0 inset-y-3 w-1.5 cursor-e-resize" },
  { dir: "nw", cls: "top-0 left-0 h-3 w-3 cursor-nw-resize" },
  { dir: "ne", cls: "top-0 right-0 h-3 w-3 cursor-ne-resize" },
  { dir: "sw", cls: "bottom-0 left-0 h-3 w-3 cursor-sw-resize" },
  { dir: "se", cls: "bottom-0 right-0 h-3 w-3 cursor-se-resize" },
];

export const FloatWindow = ({ title, zh = true, testId = "float", onClose, children }) => {
  const offRef = useRef((stackCount % 6) * 32);
  const myDef = useCallback(() => {
    const r = defRect();
    return { ...r, x: Math.min(r.x + offRef.current, Math.max(8, window.innerWidth - r.w - 8)), y: Math.min(r.y + offRef.current, Math.max(8, window.innerHeight - 120)) };
  }, []);
  const [rect, setRect] = useState(myDef);
  const rectRef = useRef(rect);
  rectRef.current = rect;
  React.useEffect(() => { stackCount += 1; return () => { stackCount = Math.max(0, stackCount - 1); }; }, []);

  const track = useCallback((onMove) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    const sx = e.clientX, sy = e.clientY, or = { ...rectRef.current };
    const move = (ev) => onMove(or, ev.clientX - sx, ev.clientY - sy);
    const up = () => { window.removeEventListener("pointermove", move); window.removeEventListener("pointerup", up); };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  }, []);

  const startDrag = track((or, dx, dy) => {
    setRect({ ...or,
      x: Math.min(window.innerWidth - 80, Math.max(-or.w + 120, or.x + dx)),
      y: Math.min(window.innerHeight - 44, Math.max(0, or.y + dy)) });
  });
  const startResize = (dir) => track((or, dx, dy) => {
    let { x, y, w, h } = or;
    if (dir.includes("e")) w = Math.max(320, or.w + dx);
    if (dir.includes("s")) h = Math.max(220, or.h + dy);
    if (dir.includes("w")) { w = Math.max(320, or.w - dx); x = or.x + (or.w - w); }
    if (dir.includes("n")) { h = Math.max(220, or.h - dy); y = or.y + (or.h - h); }
    setRect({ x, y, w, h });
  });

  return (
    <div data-testid={`${testId}-float-window`} role="dialog"
      className="fixed z-[45] rounded-2xl bg-white border border-slate-300 shadow-2xl shadow-black/25 flex flex-col overflow-hidden"
      style={{ left: rect.x, top: rect.y, width: rect.w, height: rect.h, touchAction: "none" }}>
      <div data-testid={`${testId}-float-header`}
        onPointerDown={startDrag} onDoubleClick={() => setRect(myDef())}
        className="h-10 shrink-0 flex items-center gap-2 px-3 bg-slate-50 border-b border-slate-200 cursor-move select-none">
        <GripHorizontal className="h-4 w-4 text-slate-300" />
        <p className="text-xs font-bold text-slate-700 truncate">{title}</p>
        <p className="text-[10px] text-slate-400 truncate hidden sm:block">{zh ? "拖拽移动 · 边缘调整大小 · 双击复原" : "Drag · resize edges · dbl-click reset"}</p>
        <button data-testid={`${testId}-float-close`} onPointerDown={(e) => e.stopPropagation()} onClick={onClose}
          className="ml-auto h-7 w-7 rounded-full flex items-center justify-center text-slate-400 hover:text-white hover:bg-red-500 transition-colors">
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto p-3 bg-slate-100/60">{children}</div>
      {HANDLES.map((hd) => (
        <div key={hd.dir} data-testid={`${testId}-float-resize-${hd.dir}`}
          onPointerDown={startResize(hd.dir)} className={`absolute z-10 ${hd.cls}`} />
      ))}
    </div>
  );
};
